import { getQZ } from './qzCompat';
import { configureQZSecurity, getPrinterConfig, findPrinterByName } from './qzConfig';

const DP_MM = 8; // 203dpi
const mm = (v) => Math.round(v * DP_MM);
const DEFAULT_WIDTH_MM = 100;
const DEFAULT_HEIGHT_MM = 25;

// Prefer readability. Allow 5 rows only if the 4-row profile fits vertically.
const BASE_MAX_ROWS_PER_STICKER = 4;

const esc = (value) =>
  String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/\^/g, ' ')
    .replace(/~/g, '-')
    .replace(/\r?\n/g, ' ')
    .trim();

const clipText = (value, max) => {
  const s = String(value ?? '').trim();
  if (!s) return '';
  if (s.length <= max) return s;
  if (max <= 3) return s.slice(0, Math.max(0, max));
  return `${s.slice(0, Math.max(0, max - 3))}...`;
};

const measureTextUnits = (value) => {
  const s = String(value ?? '');
  let units = 0;

  for (const ch of s) {
    if (ch === ' ') {
      units += 0.42;
    } else if (/[,.-]/.test(ch)) {
      units += 0.5;
    } else if (/[ilΙίϊ1]/u.test(ch)) {
      units += 0.62;
    } else if (/[MWΩΘΦΨΞ@%&]/u.test(ch)) {
      units += 1.18;
    } else if (/\d/.test(ch)) {
      units += 0.86;
    } else if (/[A-ZΑ-ΩΆΈΉΊΌΎΏ]/u.test(ch)) {
      units += 1.02;
    } else {
      units += 0.92;
    }
  }

  return units;
};

const clipTextByUnits = (value, maxUnits) => {
  const s = String(value ?? '').trim();
  if (!s) return '';
  if (measureTextUnits(s) <= maxUnits) return s;

  let out = '';
  let used = 0;

  for (const ch of s) {
    const next = measureTextUnits(ch);
    if ((used + next + 1.8) > maxUnits) break;
    out += ch;
    used += next;
  }

  return out ? `${out.trimEnd()}...` : '...';
};

const fitByMeasuredDots = (
  value,
  widthDots,
  fontW,
  {
    hardCap = 64,
    paddingDots = 6,
    dotScale = 0.62,
    normalizer = (s) => s
  } = {}
) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  const safe = normalizer(raw).slice(0, hardCap);
  if (!safe) return '';

  const available = Math.max(8, widthDots - paddingDots);
  const dotFactor = Math.max(5, fontW * dotScale);
  const dotsFor = (txt) => measureTextUnits(txt) * dotFactor;

  if (dotsFor(safe) <= available) return safe;

  let out = '';
  for (const ch of safe) {
    if (dotsFor(`${out}${ch}...`) > available) break;
    out += ch;
  }

  const trimmed = out.replace(/[\s,.;:-]+$/u, '');
  return trimmed ? `${trimmed}...` : '...';
};

const fitNameSmart = (value, widthDots, fontW, hardCap = 64) =>
  fitByMeasuredDots(value, widthDots, fontW, {
    hardCap,
    paddingDots: 6,
    dotScale: 0.64
  });

const normalizeTablesText = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  return raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .join(', ');
};

const fitTablesSmart = (value, widthDots, fontW, hardCap = 96) =>
  fitByMeasuredDots(value, widthDots, fontW, {
    hardCap,
    paddingDots: 14,
    dotScale: 0.74,
    normalizer: normalizeTablesText
  });

// More permissive than the old fitTextToDots logic.
// Keeps large left-side fonts and only adds "..." when really needed.
const fitEllipsisText = (value, widthDots, fontW, hardCap = 64, densityOverride = null) => {
  const s = String(value ?? '').trim();
  if (!s) return '';

  const available = Math.max(8, widthDots - 2);
  const numericLike = /^[\d\s,.-]+$/.test(s);
  const density = densityOverride ?? (numericLike ? 0.5 : 0.56);
  const avgCharDots = Math.max(4, Math.round(fontW * density));
  const maxChars = Math.max(1, Math.min(hardCap, Math.floor(available / avgCharDots)));

  return clipText(s, maxChars);
};

// For fields that must always stay visible (meat ID, cook type).
// Keep the default width whenever it already fits; otherwise compress only as much as needed.
const fitAlwaysVisible = (value, widthDots, baseW, minW = 5, hardCap = 64) => {
  const s = String(value ?? '').trim();
  if (!s) return { text: '', w: baseW };

  const safe = s.slice(0, hardCap);
  const available = Math.max(8, widthDots - 4);
  const density = 0.62;
  const baseAvgCharDots = Math.max(5, Math.round(baseW * density));

  if ((safe.length * baseAvgCharDots) <= available) {
    return { text: safe, w: baseW };
  }

  const targetW = Math.floor(available / Math.max(1, safe.length * density));

  return {
    text: safe,
    w: Math.max(minW, Math.min(baseW, targetW || baseW))
  };
};

// For meat names: try to keep all text first by compressing width a bit;
// only if still impossible, use ellipsis.
const fitFlexibleName = (value, widthDots, baseW, minW = 8, hardCap = 64) => {
  const s = String(value ?? '').trim();
  if (!s) return { text: '', w: baseW };

  const safe = s.slice(0, hardCap);
  const available = Math.max(8, widthDots - 4);
  const density = 0.6;
  const targetW = Math.floor(available / Math.max(1, safe.length * density));

  if (targetW >= minW) {
    return {
      text: safe,
      w: Math.min(baseW, targetW || baseW)
    };
  }

  const finalW = minW;
  const avgCharDots = Math.max(5, Math.round(finalW * density));
  const maxChars = Math.max(1, Math.min(hardCap, Math.floor(available / avgCharDots)));

  return {
    text: clipText(safe, maxChars),
    w: finalW
  };
};

const pageLetter = (pageIndex) => {
  let n = Math.max(1, Number(pageIndex || 1));
  let out = '';
  while (n > 0) {
    n -= 1;
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26);
  }
  return out;
};

const splitEvenly = (items, maxRowsPerSticker) => {
  const total = Array.isArray(items) ? items.length : 0;
  if (!total) return [];

  const labelCount = Math.ceil(total / maxRowsPerSticker);
  const base = Math.floor(total / labelCount);
  const extra = total % labelCount;

  const groups = [];
  let cursor = 0;

  for (let i = 0; i < labelCount; i++) {
    const size = base + (i < extra ? 1 : 0);
    groups.push(items.slice(cursor, cursor + size));
    cursor += size;
  }

  return groups;
};

const normalizeMeatRow = (item = {}) => ({
  meatId: String(
    item.meatId ??
    item.id ??
    item.assignedmeat ??
    item.meat_id ??
    ''
  ).trim(),
  requestedQuantity: String(
    item.requestedQuantity ??
    item.requested_quantity ??
    item.quantity ??
    item.qty ??
    item.grams ??
    ''
  ).trim(),
  actualQuantity: String(
    item.actualQuantity ??
    item.actual_quantity ??
    item.actual_qty ??
    item.quantity ??
    item.qty ??
    item.grams ??
    ''
  ).trim(),
  cookType: String(
    item.cookType ??
    item.cook_type ??
    item.cooktype_name ??
    item.cooktype ??
    ''
  ).trim(),
  meatName: String(
    item.meatName ??
    item.meat_name ??
    item.meat ??
    ''
  ).trim()
});

const normalizePayload = (payload = {}) => {
  const meatsInput =
    Array.isArray(payload.meats) && payload.meats.length
      ? payload.meats
      : [payload];

  const meats = meatsInput
    .map(normalizeMeatRow)
    .filter((row) => row.meatId || row.requestedQuantity || row.actualQuantity || row.cookType || row.meatName);

  return {
    reservationId: String(payload.reservationId ?? payload.reservation ?? '').trim(),
    arrivalTime: String(payload.arrivalTime ?? payload.arrival ?? '').trim(),
    tables: String(payload.tables ?? '').trim(),
    fullName: String(payload.fullName ?? payload.client ?? '').trim(),
    meats
  };
};

// LEFT side stays large always.
const LEFT_PROFILE = {
  leftId: 48,
  leftTime: 34,
  leftMeta: 32,
  leftName: 28,
  icon: 18,
  gapAfterId: 14,
  gapAfterMeta: 17
};

// RIGHT side shrinks only when needed.
// For 1-3 rows, keep the same layout but use clearly larger and wider text.
const getRightProfile = (rowCount) => {
  if (rowCount <= 1) {
    return {
      rowFont: 40,
      rowW: 31,
      rowH: 118,
      rowPadY: 26
    };
  }

  if (rowCount === 2) {
    return {
      rowFont: 34,
      rowW: 27,
      rowH: 78,
      rowPadY: 16
    };
  }

  if (rowCount === 3) {
    return {
      rowFont: 29,
      rowW: 23,
      rowH: 52,
      rowPadY: 9
    };
  }

  // same profile for 4 and 5 rows; row height still allows slightly larger/wider text.
  return {
    rowFont: 23,
    rowW: 17,
    rowH: 39,
    rowPadY: 6
  };
};

const canFitFiveRowsAtFourRowSize = (heightMm = DEFAULT_HEIGHT_MM) => {
  const H = mm(heightMm);
  const rp = getRightProfile(4);
  return (5 * rp.rowH) <= (H - 4);
};

const getMaxRowsPerSticker = (heightMm = DEFAULT_HEIGHT_MM) => {
  return canFitFiveRowsAtFourRowSize(heightMm) ? 5 : BASE_MAX_ROWS_PER_STICKER;
};

const field = (x, y, h, w, width, maxLines, align, text) =>
  `^FO${x},${y}^A0N,${h},${w}^FB${width},${maxLines},0,${align},0^FD${esc(text)}^FS`;

const fieldBold = (x, y, h, w, width, maxLines, align, text) =>
  field(x, y, h, w, width, maxLines, align, text) +
  field(x + 1, y, h, w, width, maxLines, align, text);

const drawClockIcon = (x, y, s) => {
  const c = Math.floor(s / 2);
  return [
    `^FO${x},${y}^GB${s},${s},2,B,4^FS`,
    `^FO${x + c},${y + 3}^GB2,${Math.max(4, c - 2)},2,B,0^FS`,
    `^FO${x + c},${y + c}^GB${Math.max(4, Math.floor(s * 0.25))},2,2,B,0^FS`
  ].join('');
};

function buildStickerZpl(payload, opts = {}) {
  const {
    widthMm = DEFAULT_WIDTH_MM,
    heightMm = DEFAULT_HEIGHT_MM,
    orientation = (localStorage.getItem('labelOrientation') || 'N').toUpperCase()
  } = opts;

  const p = normalizePayload(payload);
  const W = mm(widthMm);
  const H = mm(heightMm);

  // slightly smaller left side; most width goes to the meat table.
  const dividerX1 = Math.round(W * 0.385);
  const dividerX2 = dividerX1 + 4;

  const leftX = mm(0.75);
  const rightX = dividerX2 + mm(0.45);
  const rightMaxX = W - mm(0.45);

  const leftWidth = dividerX1 - leftX - mm(0.5);
  const rightWidth = rightMaxX - rightX;

  const rows = Math.max(1, p.meats.length);
  const hasName = Boolean(p.fullName);

  const lp = LEFT_PROFILE;
  const rp = getRightProfile(rows);

  const leftBlockH =
    lp.leftId +
    lp.gapAfterId +
    lp.leftMeta +
    (hasName ? lp.gapAfterMeta + lp.leftName : 0);

  const leftStartY = Math.max(6, Math.floor((H - leftBlockH) / 2));
  const line1Y = leftStartY;
  const line2Y = line1Y + lp.leftId + lp.gapAfterId;
  const line3Y = line2Y + lp.leftMeta + lp.gapAfterMeta;

  const iconSize = lp.icon;

  const timeWidth = 88;
  const timeGap = 8;
  const timeBlockWidth = iconSize + timeGap + timeWidth;
  const clockX = dividerX1 - timeBlockWidth - 10;
  const timeX = clockX + iconSize + timeGap;

  const idWidth = Math.max(72, clockX - leftX - 10);

  const tblPrefix = 'Tbls:';
  const tblPrefixW = 50;
  const tblValueX = leftX + tblPrefixW;
  const tblValueWidth = leftWidth - (tblValueX - leftX);

  const gridH = rows * rp.rowH;
  const gridTopShift = rows >= 4 ? 9 : 3;
  const gridY = Math.max(0, Math.floor((H - gridH) / 2) - gridTopShift);

  // Rebalance the right side for 5 columns: ID | Requested | Actual | Cook Type | Meat Name.
  const colIdW = 32;
  const colReqQtyW = 56;
  const colActualQtyW = 56;
  const colCookW = 96;
  const colNameW = Math.max(120, rightWidth - colIdW - colReqQtyW - colActualQtyW - colCookW);

  const col1 = rightX;
  const col2 = col1 + colIdW;
  const col3 = col2 + colReqQtyW;
  const col4 = col3 + colActualQtyW;
  const col5 = col4 + colCookW;

  const rightBoost = rows <= 2 ? 2 : rows === 3 ? 1 : 3;
  const widthBoost = rows <= 1 ? 9 : rows === 2 ? 8 : rows === 3 ? 5 : 4;

  const rightRowFont = rows >= 4 ? rp.rowFont + 3 : rp.rowFont + rightBoost;
  const baseRightW = Math.max(14, rp.rowW + widthBoost);
  const meatNameBaseW = rows <= 1
    ? Math.min(44, baseRightW + 13)
    : rows === 2
      ? Math.min(38, baseRightW + 10)
      : rows === 3
        ? Math.min(32, baseRightW + 7)
        : Math.min(28, baseRightW + 7);

  const reservationDisplay = (() => {
    const idCore = p.reservationId || '-';
    if ((payload.pageCount || 1) > 1) {
      return `#${idCore} ${pageLetter(payload.pageIndex || 1)}`;
    }
    return `#${idCore}`;
  })();

  // Left side: only ellipsize when truly necessary.
  const tablesDisplay = fitTablesSmart(
    p.tables || '-',
    tblValueWidth,
    Math.round(lp.leftMeta * 0.72),
    96
  ) || '-';

  const nameDisplay = fitNameSmart(
    p.fullName || '',
    leftWidth,
    Math.round(lp.leftName * 0.66),
    64
  );

  let out = '';
  out += '^XA';
  out += '^CI28';
  out += `^PW${W}`;
  out += `^LL${H}`;
  out += '^LH0,0';
  out += '^FWN';
  out += `^PO${orientation}`;
  out += '^PR5';

  // Full-height double divider
  out += `^FO${dividerX1},0^GB2,${H},2,B,0^FS`;
  out += `^FO${dividerX2},0^GB2,${H},2,B,0^FS`;

  // LEFT SIDE
  out += fieldBold(
    leftX,
    line1Y,
    lp.leftId,
    Math.round(lp.leftId * 0.72),
    idWidth,
    1,
    'L',
    reservationDisplay
  );

  out += drawClockIcon(
    clockX,
    line1Y + Math.max(1, Math.floor((lp.leftId - iconSize) / 2)),
    iconSize
  );

  out += field(
    timeX,
    line1Y + Math.max(0, Math.floor((lp.leftId - lp.leftTime) / 2)),
    lp.leftTime,
    Math.round(lp.leftTime * 0.72),
    timeWidth,
    1,
    'L',
    p.arrivalTime || '-'
  );

  out += field(
    leftX,
    line2Y,
    lp.leftMeta,
    Math.round(lp.leftMeta * 0.68),
    tblPrefixW,
    1,
    'L',
    tblPrefix
  );

  out += fieldBold(
    tblValueX,
    line2Y,
    lp.leftMeta,
    Math.round(lp.leftMeta * 0.72),
    tblValueWidth,
    1,
    'L',
    tablesDisplay
  );

  if (hasName) {
    out += field(
      leftX,
      line3Y,
      lp.leftName,
      Math.round(lp.leftName * 0.66),
      leftWidth,
      1,
      'C',
      nameDisplay
    );
  }

  // RIGHT SIDE: full-height vertical borders
  out += `^FO${col2},0^GB2,${H},2,B,0^FS`;
  out += `^FO${col3},0^GB2,${H},2,B,0^FS`;
  out += `^FO${col4},0^GB2,${H},2,B,0^FS`;
  out += `^FO${col5},0^GB2,${H},2,B,0^FS`;

  // horizontal borders only between rows
  for (let i = 1; i < rows; i++) {
    const y = gridY + i * rp.rowH;
    out += `^FO${rightX},${y}^GB${rightWidth},2,2,B,0^FS`;
  }

  // Meat rows
  p.meats.forEach((row, idx) => {
    const y = gridY + idx * rp.rowH + rp.rowPadY;

    const meatIdFit = fitAlwaysVisible(
      row.meatId || '-',
      colIdW - 6,
      Math.max(baseRightW, rows >= 4 ? 17 : baseRightW),
      8,
      8
    );

    const requestedQtyFit = fitAlwaysVisible(
      row.requestedQuantity || '-',
      colReqQtyW - 6,
      Math.max(baseRightW - 1, rows >= 4 ? 16 : baseRightW - 1),
      9,
      8
    );

    const actualQtyFit = fitAlwaysVisible(
      row.actualQuantity || row.requestedQuantity || '-',
      colActualQtyW - 6,
      Math.max(baseRightW, rows >= 4 ? 17 : baseRightW),
      9,
      8
    );

    const cookTypeFit = fitAlwaysVisible(
      row.cookType || '-',
      colCookW - 8,
      Math.max(baseRightW, rows >= 4 ? 17 : baseRightW),
      11,
      24
    );

    const meatNameFit = fitFlexibleName(
      row.meatName || '-',
      colNameW - 4,
      meatNameBaseW,
      12,
      40
    );

    out += field(
      col1 + 3,
      y,
      rightRowFont,
      Math.max(meatIdFit.w + (rows >= 4 ? 1 : 0), meatIdFit.w),
      colIdW - 7,
      1,
      'L',
      meatIdFit.text || '-'
    );

    out += field(
      col2 + 2,
      y,
      rightRowFont,
      Math.max(requestedQtyFit.w + (rows >= 4 ? 1 : 0), requestedQtyFit.w),
      colReqQtyW - 5,
      1,
      'C',
      requestedQtyFit.text || '-'
    );

    out += fieldBold(
      col3 + 2,
      y,
      rightRowFont,
      Math.max(actualQtyFit.w + (rows >= 4 ? 1 : 0), actualQtyFit.w),
      colActualQtyW - 5,
      1,
      'C',
      actualQtyFit.text || '-'
    );

    out += field(
      col4 + 2,
      y,
      rightRowFont,
      Math.max(cookTypeFit.w + (rows >= 4 ? 1 : 0), cookTypeFit.w),
      colCookW - 5,
      1,
      'L',
      cookTypeFit.text || '-'
    );

    out += fieldBold(
      col5 + 2,
      y,
      rightRowFont,
      Math.max(meatNameFit.w + (rows >= 4 ? 1 : 0), meatNameFit.w),
      colNameW - 5,
      1,
      'L',
      meatNameFit.text || '-'
    );
  });

  out += '^PQ1,0,1,Y^XZ';
  return out;
}

export async function printSticker(payload, opts = {}) {
  const {
    printerHint,
    failoverToDefault = true,
    widthMm = DEFAULT_WIDTH_MM,
    heightMm = DEFAULT_HEIGHT_MM
  } = opts;

  const normalized = normalizePayload(payload);

  if (!normalized.meats.length) {
    throw new Error('No meats provided for sticker printing.');
  }

  const maxRowsPerSticker = getMaxRowsPerSticker(heightMm);
  const pages = splitEvenly(normalized.meats, maxRowsPerSticker);

  try {
    const qz = await getQZ();
    await configureQZSecurity();

    const printerName =
      (printerHint ? await findPrinterByName(printerHint) : null) ||
      (failoverToDefault ? null : null);

    const cfg = await getPrinterConfig(printerName);

    for (let i = 0; i < pages.length; i++) {
      const zpl = buildStickerZpl(
        {
          ...normalized,
          meats: pages[i],
          pageIndex: i + 1,
          pageCount: pages.length
        },
        { widthMm, heightMm }
      );

      await qz.print(cfg, [zpl]);
    }

    return pages.length;
  } catch (err) {
    console.warn('[print] QZ failed (silent, no browser fallback):', err);
    throw err;
  }
}